/**
 * The invoicing profile a purchase must carry — form shape, wire payload, and the
 * completeness rule, in ONE place for every door that sells something.
 *
 * The rule itself lives on the BACKEND (`hub.tenancy.profile.assert_billing_complete`,
 * which refuses the sale); this is its mirror, and it exists for a UX reason only:
 * to disable "Avanti"/"Paga" and name the missing field BEFORE the round-trip. When
 * the two disagree the backend wins — never relax a check here to "unblock" a form.
 *
 * The fiscal shape (owner, 2026-07-28, *"a chi facciamo fattura?"*):
 *
 * - always: country, address, city, ZIP — and the province when the country is Italy
 *   (many countries have none; demanding one would refuse the sale to a buyer who
 *   cannot supply it);
 * - business: legal name + VAT, plus — only in Italy — an e-invoice channel, either
 *   the 7-char SDI code OR a PEC (alternatives, never both);
 * - private: first AND last name apart (they go on the invoice header), plus the
 *   codice fiscale when Italian — an Italian consumer's invoice is delivered to the
 *   fiscal drawer, which is indexed on it. A foreign private has none, so the field
 *   stays visible and optional rather than locking every foreign consumer out.
 */

export type EntityType = "private" | "business";

export interface BillingForm {
  entity_type: EntityType;
  legal_name: string;
  first_name: string;
  last_name: string;
  vat_number: string;
  tax_code: string;
  pec: string;
  sdi_code: string;
  billing_email: string;
  street: string;
  city: string;
  zip: string;
  province: string;
  country: string;
}

/** The wire shape (mirrors the backend `TenantProfileIn`). */
export interface BillingPayload {
  entity_type: EntityType;
  legal_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  vat_number?: string | null;
  tax_code?: string | null;
  pec?: string | null;
  sdi_code?: string | null;
  billing_email?: string | null;
  address?: {
    street?: string | null;
    city?: string | null;
    zip?: string | null;
    province?: string | null;
    country?: string | null;
  } | null;
}

export const DEFAULT_COUNTRY = "IT";

export const EMPTY_BILLING: BillingForm = {
  entity_type: "business",
  legal_name: "",
  first_name: "",
  last_name: "",
  vat_number: "",
  tax_code: "",
  pec: "",
  sdi_code: "",
  billing_email: "",
  street: "",
  city: "",
  zip: "",
  province: "",
  country: DEFAULT_COUNTRY,
};

export const isItaly = (b: BillingForm): boolean => b.country.toUpperCase() === DEFAULT_COUNTRY;
export const isBusiness = (b: BillingForm): boolean => b.entity_type === "business";
/** SDI / PEC are a real choice only for an Italian business; elsewhere the backend
 *  derives the Codice Destinatario (`0000000` / `XXXXXXX`), so no field is shown. */
export const showEinvoicing = (b: BillingForm): boolean => isItaly(b) && isBusiness(b);

const filled = (v: string): boolean => v.trim().length > 0;

/** The missing field KEYS, in the order the form renders them. Empty ⇒ sellable. */
export function missingBillingFields(b: BillingForm): string[] {
  const missing: string[] = [];
  if (isBusiness(b)) {
    if (!filled(b.legal_name)) missing.push("legal_name");
    if (!filled(b.vat_number)) missing.push("vat_number");
  } else {
    if (!filled(b.first_name)) missing.push("first_name");
    if (!filled(b.last_name)) missing.push("last_name");
  }
  if (!filled(b.country)) missing.push("country");
  if (!filled(b.street)) missing.push("street");
  if (!filled(b.city)) missing.push("city");
  if (!filled(b.zip)) missing.push("zip");
  if (isItaly(b) && !filled(b.province)) missing.push("province");
  if (isItaly(b)) {
    // An Italian business needs somewhere to deliver the e-invoice; an Italian
    // private needs the codice fiscale it is filed under.
    if (isBusiness(b)) {
      if (!filled(b.sdi_code) && !filled(b.pec)) missing.push("sdi_or_pec");
    } else if (!filled(b.tax_code)) missing.push("tax_code");
  }
  return missing;
}

export const billingComplete = (b: BillingForm): boolean => missingBillingFields(b).length === 0;

/** Form → wire. Blank strings become `null`, and the fields that do not apply to this
 *  buyer are dropped rather than sent empty. */
export function toBillingPayload(b: BillingForm): BillingPayload {
  const or = (v: string) => v.trim() || null;
  const business = isBusiness(b);
  const einvoice = showEinvoicing(b);
  return {
    entity_type: b.entity_type,
    legal_name: business ? or(b.legal_name) : null,
    first_name: business ? null : or(b.first_name),
    last_name: business ? null : or(b.last_name),
    vat_number: business ? or(b.vat_number) : null,
    tax_code: or(b.tax_code),
    sdi_code: einvoice ? or(b.sdi_code) : null,
    pec: einvoice ? or(b.pec) : null,
    billing_email: or(b.billing_email),
    address: {
      street: or(b.street),
      city: or(b.city),
      zip: or(b.zip),
      province: or(b.province),
      country: b.country || null,
    },
  };
}
